import { checkNullString } from '@/pages/datalab/utils/utils';
import type { IColumnInfo, IColumnRawInfo } from '@dbl/core/dataSource';
import { ChartType, type TChartType } from '@dbl/core/chart';
import { ChartTypeInfos } from '@dbl/core/chartEditor';
import type { TBatchAddTargetKey } from '../types';
import { getFieldDragIdentity } from './fieldIdentity';
import { isSensitiveFieldBlocked } from './isSensitiveFieldBlocked';

/**
 * 批量下拉字段 disable 规则中心。
 * UI 层（buildRegionSelectOptions）与写回层（sanitizeSelectedMarkets / applyBatchFields）
 * 共用 evaluateBatchFieldDisable，避免两套校验口径不一致。
 */

/** 批量下拉字段不可选原因，用于 disabled 与 bubble 文案映射 */
export type TBatchFieldDisableReason =
  | 'sensitive' // 敏感字段，filter/维度/X轴/展示维度禁止
  | 'xaxis_date_only' // X 轴图表要求 date，非 date 不可选
  | 'show_dimension_no_datetime' // DimNumberTrend 展示维度首项不能为 date/time
  | 'chart_limit' // 超过 ChartTypeInfos 配置的数量上限
  | 'trend_dimension_date_first' // Trend 类空操作区，首个新增维度须为 date
  | 'trend_dimension_invalid_first'; // Trend 类操作区首维已为非 date，禁止继续新增

export interface IBatchFieldDisableContext {
  targetKey: TBatchAddTargetKey;
  chartType: string;
  existingColumns: IColumnInfo[];
  /** 下拉当前勾选 key（含打开前已存在的项） */
  selectedKeys: Set<string>;
  /** 打开下拉时的操作区快照，用于计算 retained 顺序 */
  openSnapshotKeys: Set<string>;
}

/** sanitize 增量校验时 selectedKeys 由函数内部维护，调用方无需传入 */
export type IBatchFieldDisableBaseContext = Omit<IBatchFieldDisableContext,'selectedKeys'>;

/** 从 ChartTypeInfos 读取当前图表对指标/维度的数量上限规则 */
export const getChartLimit = (
  targetKey: TBatchAddTargetKey,
  chartType: string,
): { operator?: string; count?: number; } | null => {
  const chartInfo = ChartTypeInfos[chartType];
  if (!chartInfo) {
    return null;
  }
  // 各落区对应 ChartTypeInfos 不同字段；columnDimensions 优先 dimension，无则回退 xAxis
  if (targetKey === 'columnMetrics') {
    return chartInfo.metric || null;
  }
  if (targetKey === 'columnDimensions') {
    return chartInfo.dimension || chartInfo.xAxis || null;
  }
  if (targetKey === 'columnX') {
    return chartInfo.xAxis || null;
  }
  if (targetKey === 'columnShowDimensions') {
    return chartInfo.showDimension || null;
  }
  // columnFilters / columnRawColumns 等无图表数量上限配置
  return null;
};

/**
 * 判断「若再增加字段后总数为 nextCount」是否超过图表上限。
 * @param limit - ChartTypeInfos 中的 metric/dimension 等规则，operator 目前仅支持 <= / ===
 * @param nextCount - 假设再选 1 个后的总勾选数（disable）或 retained+newItems+1（写回）
 * @returns true 表示 nextCount 已超限，该字段不可再选
 */
export const isOverChartLimit = (
  limit: { operator?: string; count?: number; } | null,
  nextCount: number,
): boolean => {
  // 无 operator 或未配置 count 视为不限量
  if (!limit?.operator || limit.count === null || limit.count === undefined) {
    return false;
  }
  // ChartTypeInfos 约定：operator 为 <= 或 === 时，nextCount 大于 count 即超限
  if (limit.operator === '<=' || limit.operator === '===') {
    return nextCount > limit.count;
  }
  return false;
};

const getRetainedColumns = (ctx: IBatchFieldDisableContext): IColumnInfo[] =>
  (ctx.existingColumns || [])
    .filter(item => !checkNullString(item.id))
    // 仍勾选的操作区既有字段，用于 Trend 首维 / 展示维度首项等顺序相关规则
    .filter(item => ctx.selectedKeys.has(getFieldDragIdentity(item)));

/** 相对打开快照的新增勾选数（不含取消勾选的既有项，用于判断「首个新增」场景） */
const countNewSelectedKeys = (ctx: IBatchFieldDisableContext): number => {
  let count = 0;
  ctx.selectedKeys.forEach(key => {
    // 不在 openSnapshotKeys 中 = 本次下拉新勾选的字段
    if (!ctx.openSnapshotKeys.has(key)) {
      count += 1;
    }
  });
  return count;
};

/**
 * 判断候选字段在批量下拉中是否应 disable。
 * 已勾选项始终可选（允许取消）；未勾选项按图表规则拦截。
 */
export const evaluateBatchFieldDisable = (
  field: IColumnRawInfo,
  ctx: IBatchFieldDisableContext,
): TBatchFieldDisableReason | null => {
  const fieldKey = getFieldDragIdentity(field);

  // 已勾选项不禁用，保证用户可取消勾选
  if (ctx.selectedKeys.has(fieldKey)) {
    return null;
  }

  if (isSensitiveFieldBlocked(ctx.targetKey, field)) {
    return 'sensitive';
  }

  // X 轴落区且 ChartTypeInfos.xAxis.type === 'date' 时，只允许 date 字段
  const xAxisRequiresDate =
    ctx.targetKey === 'columnX' &&
    ChartTypeInfos[ctx.chartType || '']?.xAxis?.type === 'date';
  if (xAxisRequiresDate && field.column_type !== 'date') {
    return 'xaxis_date_only';
  }

  const retained = getRetainedColumns(ctx);
  const newSelectedCount = countNewSelectedKeys(ctx);

  // DimNumberTrend 展示维度：首项不能是 date/time（与 showDimensionSortable 落库校验一致）
  if (
    ctx.targetKey === 'columnShowDimensions' &&
    ctx.chartType === ChartType.DimNumberTrend &&
    ['date', 'time'].includes(field.column_type || '')
  ) {
    /**
     * wouldBeFirst：操作区无 retained 且尚无本次新增勾选 → 该字段将成为展示维度第一项
     * retained.length === 0：操作区原有字段均未勾选保留
     * newSelectedCount === 0：draft 里还没有相对快照的新增 key
     */
    const wouldBeFirst = retained.length === 0 && newSelectedCount === 0;
    if (wouldBeFirst) {
      return 'show_dimension_no_datetime';
    }
  }

  const limitRule = getChartLimit(ctx.targetKey, ctx.chartType);
  // 按「当前已选 + 再选 1 个」判断上限，故全选须走 sanitize 增量校验而非静态 disabled 集合
  if (isOverChartLimit(limitRule, ctx.selectedKeys.size + 1)) {
    return 'chart_limit';
  }

  // Trend / DimNumberTrend 的分类维度落区，需校验首维必须为 date
  const isTrendDimensionTarget =
    ctx.targetKey === 'columnDimensions' &&
    [ChartType.Trend, ChartType.DimNumberTrend].includes(ctx.chartType as TChartType);

  if (isTrendDimensionTarget) {
    /**
     * 操作区仍保留的首维（retained[0]）已为非 date → 整个维度区不可再新增
     * 对应 dimensionSortable 首维非法时的拖拽拦截
     */
    if (retained.length > 0 && retained[0].column_type !== 'date') {
      return 'trend_dimension_invalid_first';
    }
    /**
     * wouldBeFirstTrendDimension：空操作区 + 尚无新增勾选 + 当前候选非 date
     * → 该字段将成为 Trend 类图表的第一个分类维度，必须拦截
     */
    const wouldBeFirstTrendDimension =
      retained.length === 0 && newSelectedCount === 0 && field.column_type !== 'date';
    if (wouldBeFirstTrendDimension) {
      return 'trend_dimension_date_first';
    }
  }

  return null;
};

/**
 * 按 candidateMarkets 顺序增量校验勾选集。
 *
 * 为何不能依赖 options 里的 disabled 集合：
 * - disable 按「再选 1 个」动态计算，全选一次性加入 N 个 key 时快照会 stale；
 * - 须模拟逐个加入，每步更新 selectedSet 再调 evaluateBatchFieldDisable。
 */
export const sanitizeSelectedMarkets = (
  candidateMarkets: string[],
  baseContext: IBatchFieldDisableBaseContext,
  fieldKeyMap: Map<string, IColumnRawInfo> | null | undefined,
): string[] => {
  const result: string[] = [];
  const selectedSet = new Set<string>();

  /**
   * 第一遍：保留用户仍勾选的打开快照项。
   * 不能对快照项重跑 evaluate：candidateMarkets 顺序（如全选）可能与操作区不同，
   * 会导致 Trend 首维等顺序规则误删既有合法字段。
   */
  candidateMarkets.forEach(key => {
    if (!key || selectedSet.has(key) || !baseContext.openSnapshotKeys.has(key)) {
      return;
    }
    result.push(key);
    selectedSet.add(key);
  });

  /**
   * 第二遍：按 candidateMarkets 顺序增量校验相对快照的新增勾选。
   * disable 按「再选 1 个」动态计算，须模拟逐个加入并每步更新 selectedSet。
   */
  candidateMarkets.forEach(key => {
    if (!key || selectedSet.has(key)) {
      return;
    }
    // 第一遍已处理打开快照中的既有项
    if (baseContext.openSnapshotKeys.has(key)) {
      return;
    }

    const ctx: IBatchFieldDisableContext = {
      ...baseContext,
      selectedKeys: selectedSet,
    };

    const rawField = fieldKeyMap?.get(key);
    if (!rawField) {
      return;
    }

    // evaluateBatchFieldDisable 返回非 null 表示不可选，跳过该 key
    if (evaluateBatchFieldDisable(rawField, ctx)) {
      return;
    }

    result.push(key);
    selectedSet.add(key);
  });

  return result;
};

